import { createHash } from "node:crypto";
import {
  broadcastPrisma as prisma,
  CommunicationBroadcastChunkStatus,
  CommunicationBroadcastDeliveryStatus,
  CommunicationBroadcastDispatchStatus,
  CommunicationLogStatus,
  NotificationEntities,
  NotificationsType,
  ParticipantType,
  Provider,
  TemplateV2Type,
  type Prisma,
} from "client";
import { sendExpoNotification } from "@sajilni/utils/expoNotification";
import { createIdempotentCommunicationLog } from "../communicationLog";
import {
  filtersFromSettings,
  resolveRegisteredAudience,
} from "../communicationAudience";
import { resolvePushNotificationMergeTags } from "../communicationSend/notificationMergeTags.pure";
import { classifyNotificationPreflight } from "../communicationSend/notificationPreflight.pure";
import { publishCreatedNotification } from "../notification";

const DEFAULT_CHUNK_SIZE = 100;
const DEFAULT_RECIPIENT_CONCURRENCY = 2;
const MAX_RECIPIENT_CONCURRENCY = 5;

const TERMINAL_DELIVERY_STATUSES =
  new Set<CommunicationBroadcastDeliveryStatus>([
    CommunicationBroadcastDeliveryStatus.SUCCEEDED,
    CommunicationBroadcastDeliveryStatus.PARTIAL,
    CommunicationBroadcastDeliveryStatus.FAILED,
    CommunicationBroadcastDeliveryStatus.NO_TOKEN,
    CommunicationBroadcastDeliveryStatus.AMBIGUOUS,
  ]);
const TERMINAL_CHUNK_STATUSES = new Set<CommunicationBroadcastChunkStatus>([
  CommunicationBroadcastChunkStatus.COMPLETED,
  CommunicationBroadcastChunkStatus.COMPLETED_WITH_FAILURES,
  CommunicationBroadcastChunkStatus.FAILED,
  CommunicationBroadcastChunkStatus.CANCELLED,
]);

type DispatchChunkSummary = {
  id: string;
  chunkIndex: number;
  expectedCount: number;
  status: CommunicationBroadcastChunkStatus;
};

type DeliveryRow = {
  id: string;
  recipientProfileId: string;
  provider: Provider;
  status: CommunicationBroadcastDeliveryStatus;
  attemptCount: number;
  providerMessageId: string | null;
  notificationId: string | null;
  failedReason: string | null;
  payload: unknown;
};

export type PreparedCommunicationBroadcast = {
  dispatchId: string;
  expectedCount: number;
  chunks: DispatchChunkSummary[];
};

function positiveInteger(
  raw: string | undefined,
  fallback: number,
  maximum?: number
) {
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed <= 0) return fallback;
  return maximum ? Math.min(parsed, maximum) : parsed;
}

function chunkSize() {
  return positiveInteger(
    process.env.COMMUNICATION_FANOUT_V2_CHUNK_SIZE,
    DEFAULT_CHUNK_SIZE,
    500
  );
}

function recipientConcurrency() {
  return positiveInteger(
    process.env.COMMUNICATION_FANOUT_V2_RECIPIENT_CONCURRENCY,
    DEFAULT_RECIPIENT_CONCURRENCY,
    MAX_RECIPIENT_CONCURRENCY
  );
}

function deterministicObjectId(parts: string[]) {
  return createHash("sha256")
    .update(parts.join("/"))
    .digest("hex")
    .slice(0, 24);
}

function deliveryIdempotencyKey(input: {
  communicationSettingsId: string;
  recipientProfileId: string;
  occurrenceKey: string;
  provider: Provider;
}) {
  return `${input.communicationSettingsId}/${input.recipientProfileId}/${input.occurrenceKey}/${input.provider}`;
}

function terminal(status: CommunicationBroadcastDeliveryStatus) {
  return TERMINAL_DELIVERY_STATUSES.has(status);
}

function expoTicketCounts(payload: unknown) {
  if (!payload || typeof payload !== "object" || !("tickets" in payload)) {
    return { accepted: 0, rejected: 0 };
  }
  const tickets = (payload as { tickets?: unknown }).tickets;
  if (!Array.isArray(tickets)) return { accepted: 0, rejected: 0 };
  return tickets.reduce(
    (counts, ticket) => {
      if (
        ticket &&
        typeof ticket === "object" &&
        (ticket as { status?: string }).status === "ok"
      ) {
        counts.accepted++;
      } else {
        counts.rejected++;
      }
      return counts;
    },
    { accepted: 0, rejected: 0 }
  );
}

export async function createCommunicationBroadcastDispatch(input: {
  communicationSettingsId: string;
  eventId: string;
  occurrenceKey: string;
}) {
  return prisma.communicationBroadcastDispatch.upsert({
    where: {
      communicationSettingsId_occurrenceKey: {
        communicationSettingsId: input.communicationSettingsId,
        occurrenceKey: input.occurrenceKey,
      },
    },
    create: {
      communicationSettingsId: input.communicationSettingsId,
      eventId: input.eventId,
      occurrenceKey: input.occurrenceKey,
    },
    update: {},
    select: { id: true, status: true },
  });
}

export async function prepareCommunicationBroadcastDispatch(
  dispatchId: string
): Promise<PreparedCommunicationBroadcast> {
  const dispatch = await prisma.communicationBroadcastDispatch.findUnique({
    where: { id: dispatchId },
    select: {
      id: true,
      status: true,
      eventId: true,
      occurrenceKey: true,
      expectedCount: true,
      communicationSettings: {
        select: {
          id: true,
          audienceTicketIds: true,
          audienceInvitationIds: true,
          audienceParticipantGroupIds: true,
          audienceIncludeEntityTeamMembers: true,
          manualAudienceProfileIds: true,
          includeRegisteredAttendees: true,
        },
      },
      chunks: {
        select: {
          id: true,
          chunkIndex: true,
          expectedCount: true,
          status: true,
        },
        orderBy: { chunkIndex: "asc" },
      },
    },
  });
  if (!dispatch) throw new Error("Broadcast dispatch not found");
  if (
    dispatch.status === CommunicationBroadcastDispatchStatus.COMPLETED ||
    dispatch.status ===
      CommunicationBroadcastDispatchStatus.COMPLETED_WITH_FAILURES ||
    dispatch.status === CommunicationBroadcastDispatchStatus.CANCELLED
  ) {
    return {
      dispatchId,
      expectedCount: dispatch.expectedCount,
      chunks: dispatch.chunks,
    };
  }

  await prisma.communicationBroadcastDispatch.update({
    where: { id: dispatchId },
    data: {
      status: dispatch.chunks.length
        ? CommunicationBroadcastDispatchStatus.QUEUING
        : CommunicationBroadcastDispatchStatus.PREPARING,
      startedAt: dispatch.chunks.length ? undefined : new Date(),
      failedReason: null,
    },
  });

  if (dispatch.chunks.length) {
    return {
      dispatchId,
      expectedCount: dispatch.expectedCount,
      chunks: dispatch.chunks,
    };
  }

  const resolved = await resolveRegisteredAudience(
    {
      eventId: dispatch.eventId,
      ...filtersFromSettings(dispatch.communicationSettings),
    },
    prisma
  );
  const profileIds = [...new Set(resolved.profileIds)];
  if (profileIds.length === 0) {
    await Promise.all([
      prisma.communicationBroadcastDispatch.update({
        where: { id: dispatchId },
        data: {
          status: CommunicationBroadcastDispatchStatus.FAILED,
          failedReason: "No valid users",
          completedAt: new Date(),
        },
      }),
      prisma.communicationSettings.update({
        where: { id: dispatch.communicationSettings.id },
        data: { status: "FAILED" },
      }),
    ]);
    throw new Error("No valid users");
  }

  await prisma.communicationSettings.update({
    where: { id: dispatch.communicationSettings.id },
    data: {
      audienceProfileIds: profileIds,
      audienceSize: profileIds.length,
      audienceResolvedAt: new Date(),
    },
  });

  const size = chunkSize();
  for (let offset = 0; offset < profileIds.length; offset += size) {
    const recipients = profileIds.slice(offset, offset + size);
    const index = Math.floor(offset / size);
    await prisma.communicationBroadcastChunk.upsert({
      where: {
        dispatchId_chunkIndex: { dispatchId, chunkIndex: index },
      },
      create: {
        dispatchId,
        chunkIndex: index,
        recipientProfileIds: recipients,
        expectedCount: recipients.length,
      },
      update: {},
    });
  }

  const sourceCount =
    resolved.buyerCount +
    resolved.groupMemberCount +
    new Set(dispatch.communicationSettings.manualAudienceProfileIds).size;
  const chunks = await prisma.communicationBroadcastChunk.findMany({
    where: { dispatchId },
    select: {
      id: true,
      chunkIndex: true,
      expectedCount: true,
      status: true,
    },
    orderBy: { chunkIndex: "asc" },
  });
  await prisma.communicationBroadcastDispatch.update({
    where: { id: dispatchId },
    data: {
      status: CommunicationBroadcastDispatchStatus.QUEUING,
      expectedCount: profileIds.length,
      resolvedCount: profileIds.length,
      duplicatesRemoved: Math.max(0, sourceCount - profileIds.length),
    },
  });
  return { dispatchId, expectedCount: profileIds.length, chunks };
}

export async function markCommunicationBroadcastChunksQueued(
  dispatchId: string,
  chunkIds: string[]
) {
  if (chunkIds.length) {
    await prisma.communicationBroadcastChunk.updateMany({
      where: {
        id: { in: chunkIds },
        status: CommunicationBroadcastChunkStatus.PENDING,
      },
      data: { status: CommunicationBroadcastChunkStatus.QUEUED },
    });
  }
  const chunks = await prisma.communicationBroadcastChunk.findMany({
    where: { dispatchId },
    select: { expectedCount: true, status: true },
  });
  const queuedCount = chunks
    .filter(
      (chunk) => chunk.status !== CommunicationBroadcastChunkStatus.PENDING
    )
    .reduce((sum, chunk) => sum + chunk.expectedCount, 0);
  const dispatch = await prisma.communicationBroadcastDispatch.findUnique({
    where: { id: dispatchId },
    select: { expectedCount: true },
  });
  await prisma.communicationBroadcastDispatch.update({
    where: { id: dispatchId },
    data: {
      queuedCount,
      status:
        dispatch && queuedCount === dispatch.expectedCount
          ? CommunicationBroadcastDispatchStatus.SENDING
          : CommunicationBroadcastDispatchStatus.QUEUING,
    },
  });
}

export async function markCommunicationBroadcastDispatchFailed(
  dispatchId: string,
  reason: string,
  terminalFailure: boolean
) {
  const dispatch = await prisma.communicationBroadcastDispatch.update({
    where: { id: dispatchId },
    data: {
      retryCount: { increment: 1 },
      failedReason: reason,
      ...(terminalFailure
        ? {
            status: CommunicationBroadcastDispatchStatus.FAILED,
            completedAt: new Date(),
          }
        : {}),
    },
    select: { communicationSettingsId: true },
  });
  if (terminalFailure) {
    await prisma.communicationSettings.update({
      where: { id: dispatch.communicationSettingsId },
      data: { status: "FAILED" },
    });
  }
}

async function claimDelivery(input: {
  dispatchId: string;
  chunkId: string;
  recipientProfileId: string;
  provider: Provider;
}): Promise<{ execute: boolean; delivery: DeliveryRow }> {
  const delivery = (await prisma.communicationBroadcastDelivery.upsert({
    where: {
      dispatchId_recipientProfileId_provider: {
        dispatchId: input.dispatchId,
        recipientProfileId: input.recipientProfileId,
        provider: input.provider,
      },
    },
    create: {
      dispatchId: input.dispatchId,
      chunkId: input.chunkId,
      recipientProfileId: input.recipientProfileId,
      provider: input.provider,
    },
    update: {},
  })) as DeliveryRow;

  if (terminal(delivery.status)) return { execute: false, delivery };
  if (
    delivery.status === CommunicationBroadcastDeliveryStatus.PROCESSING &&
    input.provider === Provider.EXPO
  ) {
    const ambiguous = (await prisma.communicationBroadcastDelivery.update({
      where: { id: delivery.id },
      data: {
        status: CommunicationBroadcastDeliveryStatus.AMBIGUOUS,
        failedReason:
          "Previous Expo attempt has an ambiguous result; not retried to avoid a duplicate push",
        completedAt: new Date(),
      },
    })) as DeliveryRow;
    return { execute: false, delivery: ambiguous };
  }

  const claimed = (await prisma.communicationBroadcastDelivery.update({
    where: { id: delivery.id },
    data: {
      status: CommunicationBroadcastDeliveryStatus.PROCESSING,
      attemptCount: { increment: 1 },
      startedAt: delivery.attemptCount ? undefined : new Date(),
      failedReason: null,
    },
  })) as DeliveryRow;
  return { execute: true, delivery: claimed };
}

async function writeDeliveryLog(input: {
  delivery: DeliveryRow;
  communicationSettingsId: string;
  templateV2Id: string;
  occurrenceKey: string;
  email?: string | null;
  phoneNumber?: string | null;
  status: CommunicationLogStatus;
  failedReason?: string | null;
  providerMessageId: string;
  payload?: Prisma.InputJsonValue;
}) {
  try {
    await createIdempotentCommunicationLog(
      {
        email: input.email,
        phoneNumber: input.phoneNumber,
        templateV2Id: input.templateV2Id,
        communicationSettingsId: input.communicationSettingsId,
        recipientProfileId: input.delivery.recipientProfileId,
        occurrenceKey: input.occurrenceKey,
        provider: input.delivery.provider,
        providerMessageId: input.providerMessageId,
        status: input.status,
        failedReason: input.failedReason,
        idempotencyKey: deliveryIdempotencyKey({
          communicationSettingsId: input.communicationSettingsId,
          recipientProfileId: input.delivery.recipientProfileId,
          occurrenceKey: input.occurrenceKey,
          provider: input.delivery.provider,
        }),
        payload: input.payload,
        skipLegacyLookup: true,
      },
      prisma
    );
  } catch (error) {
    // The delivery ledger is the source of truth for V2 progress. Keep the
    // channel terminal and surface this repairable analytics-log failure.
    await prisma.communicationBroadcastDelivery.update({
      where: { id: input.delivery.id },
      data: {
        failedReason: `CommunicationLog mirror failed: ${
          error instanceof Error ? error.message : String(error)
        }`,
      },
    });
  }
}

async function failDelivery(input: {
  dispatchId: string;
  chunkId: string;
  recipientProfileId: string;
  provider: Provider;
  communicationSettingsId: string;
  templateV2Id: string;
  occurrenceKey: string;
  reason: string;
  email?: string | null;
  phoneNumber?: string | null;
  payload?: Prisma.InputJsonValue;
}) {
  const claimed = await claimDelivery(input);
  if (!claimed.execute) return claimed.delivery;
  const delivery = (await prisma.communicationBroadcastDelivery.update({
    where: { id: claimed.delivery.id },
    data: {
      status: CommunicationBroadcastDeliveryStatus.FAILED,
      failedReason: input.reason,
      completedAt: new Date(),
    },
  })) as DeliveryRow;
  await writeDeliveryLog({
    delivery,
    communicationSettingsId: input.communicationSettingsId,
    templateV2Id: input.templateV2Id,
    occurrenceKey: input.occurrenceKey,
    email: input.email,
    phoneNumber: input.phoneNumber,
    providerMessageId: `failed-${input.occurrenceKey}`,
    status: CommunicationLogStatus.FAILED,
    failedReason: input.reason,
    payload: input.payload,
  });
  return delivery;
}

async function sendInApp(input: {
  dispatchId: string;
  chunkId: string;
  recipientProfileId: string;
  receiverUserId: string;
  communicationSettingsId: string;
  templateV2Id: string;
  occurrenceKey: string;
  eventId: string;
  email?: string | null;
  phoneNumber?: string | null;
  title: string;
  body: string;
}) {
  const claimed = await claimDelivery({ ...input, provider: Provider.IN_APP });
  const notificationId =
    claimed.delivery.notificationId ??
    deterministicObjectId([
      "broadcast-notification",
      input.dispatchId,
      input.recipientProfileId,
      input.occurrenceKey,
    ]);
  if (
    claimed.delivery.status === CommunicationBroadcastDeliveryStatus.SUCCEEDED
  ) {
    await writeDeliveryLog({
      delivery: claimed.delivery,
      communicationSettingsId: input.communicationSettingsId,
      templateV2Id: input.templateV2Id,
      occurrenceKey: input.occurrenceKey,
      email: input.email,
      phoneNumber: input.phoneNumber,
      providerMessageId: notificationId,
      status: CommunicationLogStatus.DELIVERED,
      payload: { title: input.title, body: input.body },
    });
    return;
  }
  if (!claimed.execute) return;

  try {
    let notification = await prisma.notification.findUnique({
      where: { id: notificationId },
      select: {
        id: true,
        receiverId: true,
        receiver: true,
        senderId: true,
        type: true,
        title: true,
        body: true,
      },
    });
    if (!notification) {
      notification = await prisma.notification.create({
        data: {
          id: notificationId,
          type: NotificationsType.COMMUNICATION,
          entity: NotificationEntities.COMMUNICATION,
          entityId: input.communicationSettingsId,
          receiverId: input.receiverUserId,
          sender: ParticipantType.USER,
          receiver: ParticipantType.USER,
          eventId: input.eventId,
          title: input.title,
          body: input.body,
          sentAt: new Date(),
        },
        select: {
          id: true,
          receiverId: true,
          receiver: true,
          senderId: true,
          type: true,
          title: true,
          body: true,
        },
      });
      void publishCreatedNotification(notification).catch(() => undefined);
    }
    const delivery = (await prisma.communicationBroadcastDelivery.update({
      where: { id: claimed.delivery.id },
      data: {
        status: CommunicationBroadcastDeliveryStatus.SUCCEEDED,
        notificationId: notification.id,
        providerMessageId: notification.id,
        completedAt: new Date(),
      },
    })) as DeliveryRow;
    await writeDeliveryLog({
      delivery,
      communicationSettingsId: input.communicationSettingsId,
      templateV2Id: input.templateV2Id,
      occurrenceKey: input.occurrenceKey,
      email: input.email,
      phoneNumber: input.phoneNumber,
      providerMessageId: notification.id,
      status: CommunicationLogStatus.DELIVERED,
      payload: { title: input.title, body: input.body },
    });
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    const delivery = (await prisma.communicationBroadcastDelivery.update({
      where: { id: claimed.delivery.id },
      data: {
        status: CommunicationBroadcastDeliveryStatus.FAILED,
        failedReason: reason,
        completedAt: new Date(),
      },
    })) as DeliveryRow;
    await writeDeliveryLog({
      delivery,
      communicationSettingsId: input.communicationSettingsId,
      templateV2Id: input.templateV2Id,
      occurrenceKey: input.occurrenceKey,
      email: input.email,
      phoneNumber: input.phoneNumber,
      providerMessageId: `in-app-error-${input.occurrenceKey}`,
      status: CommunicationLogStatus.FAILED,
      failedReason: reason,
      payload: { title: input.title, body: input.body },
    });
  }
}

async function sendExpo(input: {
  dispatchId: string;
  chunkId: string;
  recipientProfileId: string;
  receiverUserId: string;
  communicationSettingsId: string;
  templateV2Id: string;
  occurrenceKey: string;
  tokens: string[];
  email?: string | null;
  phoneNumber?: string | null;
  title: string;
  body: string;
}) {
  const claimed = await claimDelivery({ ...input, provider: Provider.EXPO });
  if (terminal(claimed.delivery.status)) {
    if (claimed.delivery.providerMessageId) {
      await writeDeliveryLog({
        delivery: claimed.delivery,
        communicationSettingsId: input.communicationSettingsId,
        templateV2Id: input.templateV2Id,
        occurrenceKey: input.occurrenceKey,
        email: input.email,
        phoneNumber: input.phoneNumber,
        providerMessageId: claimed.delivery.providerMessageId,
        status:
          claimed.delivery.status ===
          CommunicationBroadcastDeliveryStatus.SUCCEEDED
            ? CommunicationLogStatus.SENT
            : CommunicationLogStatus.FAILED,
        failedReason: claimed.delivery.failedReason,
        payload: claimed.delivery.payload as Prisma.InputJsonValue,
      });
    }
    return;
  }
  if (!claimed.execute) return;

  if (input.tokens.length === 0) {
    const reason = "No device tokens for this event";
    const delivery = (await prisma.communicationBroadcastDelivery.update({
      where: { id: claimed.delivery.id },
      data: {
        status: CommunicationBroadcastDeliveryStatus.NO_TOKEN,
        failedReason: reason,
        providerMessageId: `no-token-${input.occurrenceKey}`,
        completedAt: new Date(),
      },
    })) as DeliveryRow;
    await writeDeliveryLog({
      delivery,
      communicationSettingsId: input.communicationSettingsId,
      templateV2Id: input.templateV2Id,
      occurrenceKey: input.occurrenceKey,
      email: input.email,
      phoneNumber: input.phoneNumber,
      providerMessageId: `no-token-${input.occurrenceKey}`,
      status: CommunicationLogStatus.FAILED,
      failedReason: reason,
      payload: { title: input.title, body: input.body },
    });
    return;
  }

  try {
    const result = await sendExpoNotification(
      input.receiverUserId,
      input.tokens,
      input.title,
      {
        type: "COMMUNICATION",
        communicationSettingsId: input.communicationSettingsId,
        occurrenceKey: input.occurrenceKey,
      },
      input.body
    );
    const accepted = result.tickets.filter((ticket) => ticket.status === "ok");
    const rejected = result.tickets.length - accepted.length;
    const status =
      accepted.length === result.tickets.length && accepted.length > 0
        ? CommunicationBroadcastDeliveryStatus.SUCCEEDED
        : accepted.length > 0
        ? CommunicationBroadcastDeliveryStatus.PARTIAL
        : CommunicationBroadcastDeliveryStatus.FAILED;
    const failedReason =
      rejected > 0
        ? `${rejected}/${result.tickets.length} Expo tickets rejected: ${
            result.error ?? "provider rejection"
          }`
        : null;
    const providerMessageId = accepted[0]?.id ?? `expo-${input.occurrenceKey}`;
    const payload = {
      title: input.title,
      body: input.body,
      tickets: result.tickets,
    } satisfies Prisma.InputJsonValue;
    const delivery = (await prisma.communicationBroadcastDelivery.update({
      where: { id: claimed.delivery.id },
      data: {
        status,
        failedReason,
        providerMessageId,
        payload,
        completedAt: new Date(),
      },
    })) as DeliveryRow;
    await writeDeliveryLog({
      delivery,
      communicationSettingsId: input.communicationSettingsId,
      templateV2Id: input.templateV2Id,
      occurrenceKey: input.occurrenceKey,
      email: input.email,
      phoneNumber: input.phoneNumber,
      providerMessageId,
      status:
        status === CommunicationBroadcastDeliveryStatus.SUCCEEDED
          ? CommunicationLogStatus.SENT
          : CommunicationLogStatus.FAILED,
      failedReason,
      payload,
    });
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    const delivery = (await prisma.communicationBroadcastDelivery.update({
      where: { id: claimed.delivery.id },
      data: {
        status: CommunicationBroadcastDeliveryStatus.FAILED,
        failedReason: reason,
        providerMessageId: `expo-error-${input.occurrenceKey}`,
        completedAt: new Date(),
      },
    })) as DeliveryRow;
    await writeDeliveryLog({
      delivery,
      communicationSettingsId: input.communicationSettingsId,
      templateV2Id: input.templateV2Id,
      occurrenceKey: input.occurrenceKey,
      email: input.email,
      phoneNumber: input.phoneNumber,
      providerMessageId: `expo-error-${input.occurrenceKey}`,
      status: CommunicationLogStatus.FAILED,
      failedReason: reason,
      payload: { title: input.title, body: input.body },
    });
  }
}

async function runBounded<T>(
  items: T[],
  concurrency: number,
  work: (item: T) => Promise<void>
) {
  let cursor = 0;
  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    async () => {
      while (cursor < items.length) {
        const item = items[cursor++];
        await work(item);
      }
    }
  );
  await Promise.all(workers);
}

async function finishCommunicationBroadcastChunk(
  dispatchId: string,
  chunkId: string,
  expectedProfileIds: string[]
) {
  const deliveries = (await prisma.communicationBroadcastDelivery.findMany({
    where: { chunkId },
    select: {
      id: true,
      recipientProfileId: true,
      provider: true,
      status: true,
      attemptCount: true,
      providerMessageId: true,
      notificationId: true,
      failedReason: true,
      payload: true,
    },
  })) as DeliveryRow[];
  const providersByRecipient = new Map<string, Set<Provider>>();
  let inAppSucceededCount = 0;
  let inAppFailedCount = 0;
  let expoAcceptedCount = 0;
  let expoFailedCount = 0;
  let expoNoTokenCount = 0;
  let expoTicketsAccepted = 0;
  let expoTicketsRejected = 0;

  for (const delivery of deliveries) {
    if (terminal(delivery.status)) {
      const providers =
        providersByRecipient.get(delivery.recipientProfileId) ??
        new Set<Provider>();
      providers.add(delivery.provider);
      providersByRecipient.set(delivery.recipientProfileId, providers);
    }
    if (delivery.provider === Provider.IN_APP) {
      if (delivery.status === CommunicationBroadcastDeliveryStatus.SUCCEEDED) {
        inAppSucceededCount++;
      } else if (terminal(delivery.status)) {
        inAppFailedCount++;
      }
    } else if (delivery.provider === Provider.EXPO) {
      if (
        delivery.status === CommunicationBroadcastDeliveryStatus.SUCCEEDED ||
        delivery.status === CommunicationBroadcastDeliveryStatus.PARTIAL
      ) {
        expoAcceptedCount++;
      }
      if (
        delivery.status === CommunicationBroadcastDeliveryStatus.PARTIAL ||
        delivery.status === CommunicationBroadcastDeliveryStatus.FAILED ||
        delivery.status === CommunicationBroadcastDeliveryStatus.AMBIGUOUS
      ) {
        expoFailedCount++;
      }
      if (delivery.status === CommunicationBroadcastDeliveryStatus.NO_TOKEN) {
        expoNoTokenCount++;
      }
      const tickets = expoTicketCounts(delivery.payload);
      expoTicketsAccepted += tickets.accepted;
      expoTicketsRejected += tickets.rejected;
    }
  }
  const terminalCount = expectedProfileIds.filter(
    (profileId) => providersByRecipient.get(profileId)?.size === 2
  ).length;
  const hasFailures =
    inAppFailedCount > 0 ||
    expoFailedCount > 0 ||
    expoNoTokenCount > 0 ||
    terminalCount !== expectedProfileIds.length;
  await prisma.communicationBroadcastChunk.update({
    where: { id: chunkId },
    data: {
      status:
        terminalCount !== expectedProfileIds.length
          ? CommunicationBroadcastChunkStatus.FAILED
          : hasFailures
          ? CommunicationBroadcastChunkStatus.COMPLETED_WITH_FAILURES
          : CommunicationBroadcastChunkStatus.COMPLETED,
      terminalCount,
      inAppSucceededCount,
      inAppFailedCount,
      expoAcceptedCount,
      expoFailedCount,
      expoNoTokenCount,
      expoTicketsAccepted,
      expoTicketsRejected,
      failedReason:
        terminalCount === expectedProfileIds.length
          ? null
          : `${
              expectedProfileIds.length - terminalCount
            } recipients are not terminal`,
      completedAt: new Date(),
    },
  });
  await reconcileCommunicationBroadcastDispatch(dispatchId);
}

export async function reconcileCommunicationBroadcastDispatch(
  dispatchId: string
) {
  const dispatch = await prisma.communicationBroadcastDispatch.findUnique({
    where: { id: dispatchId },
    select: {
      expectedCount: true,
      communicationSettingsId: true,
      chunks: {
        select: {
          status: true,
          expectedCount: true,
          terminalCount: true,
          inAppSucceededCount: true,
          inAppFailedCount: true,
          expoAcceptedCount: true,
          expoFailedCount: true,
          expoNoTokenCount: true,
          expoTicketsAccepted: true,
          expoTicketsRejected: true,
        },
      },
    },
  });
  if (!dispatch) return;
  const sums = dispatch.chunks.reduce(
    (total, chunk) => ({
      terminalCount: total.terminalCount + chunk.terminalCount,
      inAppSucceededCount:
        total.inAppSucceededCount + chunk.inAppSucceededCount,
      inAppFailedCount: total.inAppFailedCount + chunk.inAppFailedCount,
      expoAcceptedCount: total.expoAcceptedCount + chunk.expoAcceptedCount,
      expoFailedCount: total.expoFailedCount + chunk.expoFailedCount,
      expoNoTokenCount: total.expoNoTokenCount + chunk.expoNoTokenCount,
      expoTicketsAccepted:
        total.expoTicketsAccepted + chunk.expoTicketsAccepted,
      expoTicketsRejected:
        total.expoTicketsRejected + chunk.expoTicketsRejected,
    }),
    {
      terminalCount: 0,
      inAppSucceededCount: 0,
      inAppFailedCount: 0,
      expoAcceptedCount: 0,
      expoFailedCount: 0,
      expoNoTokenCount: 0,
      expoTicketsAccepted: 0,
      expoTicketsRejected: 0,
    }
  );
  const allChunksTerminal = dispatch.chunks.every((chunk) =>
    TERMINAL_CHUNK_STATUSES.has(chunk.status)
  );
  const fullyAccounted =
    sums.terminalCount === dispatch.expectedCount && dispatch.expectedCount > 0;
  const hasFailures =
    sums.inAppFailedCount > 0 ||
    sums.expoFailedCount > 0 ||
    sums.expoNoTokenCount > 0;
  const completed = allChunksTerminal && fullyAccounted;
  const status = completed
    ? hasFailures
      ? CommunicationBroadcastDispatchStatus.COMPLETED_WITH_FAILURES
      : CommunicationBroadcastDispatchStatus.COMPLETED
    : allChunksTerminal
    ? CommunicationBroadcastDispatchStatus.FAILED
    : CommunicationBroadcastDispatchStatus.SENDING;
  await prisma.communicationBroadcastDispatch.update({
    where: { id: dispatchId },
    data: {
      ...sums,
      status,
      completedAt: allChunksTerminal ? new Date() : null,
      failedReason:
        allChunksTerminal && !fullyAccounted
          ? `${
              dispatch.expectedCount - sums.terminalCount
            } recipients are unaccounted`
          : null,
    },
  });
  if (allChunksTerminal) {
    await prisma.communicationSettings.update({
      where: { id: dispatch.communicationSettingsId },
      data: {
        status:
          status === CommunicationBroadcastDispatchStatus.COMPLETED
            ? "SENT"
            : "FAILED",
      },
    });
  }
}

export async function processCommunicationBroadcastChunk(input: {
  dispatchId: string;
  chunkId: string;
}) {
  const chunk = await prisma.communicationBroadcastChunk.findUnique({
    where: { id: input.chunkId },
    select: {
      id: true,
      dispatchId: true,
      recipientProfileIds: true,
      status: true,
      dispatch: {
        select: {
          occurrenceKey: true,
          eventId: true,
          communicationSettings: {
            select: {
              id: true,
              templateV2Id: true,
              templateV2: {
                select: {
                  type: true,
                  scopeId: true,
                  config: true,
                },
              },
              triggerSession: {
                select: {
                  translations: {
                    select: { name: true, location: true },
                    take: 1,
                  },
                },
              },
            },
          },
        },
      },
    },
  });
  if (!chunk || chunk.dispatchId !== input.dispatchId) {
    throw new Error("Broadcast chunk not found");
  }
  if (
    chunk.status === CommunicationBroadcastChunkStatus.COMPLETED ||
    chunk.status ===
      CommunicationBroadcastChunkStatus.COMPLETED_WITH_FAILURES ||
    chunk.status === CommunicationBroadcastChunkStatus.CANCELLED
  ) {
    return;
  }
  const communication = chunk.dispatch.communicationSettings;
  if (
    !communication.templateV2Id ||
    communication.templateV2?.type !== TemplateV2Type.NOTIFICATION
  ) {
    throw new Error("Broadcast chunk requires a notification TemplateV2");
  }

  await Promise.all([
    prisma.communicationBroadcastChunk.update({
      where: { id: chunk.id },
      data: {
        status: CommunicationBroadcastChunkStatus.PROCESSING,
        attemptCount: { increment: 1 },
        startedAt: new Date(),
        failedReason: null,
      },
    }),
    prisma.communicationBroadcastDispatch.updateMany({
      where: {
        id: input.dispatchId,
        OR: [
          { firstChunkStartedAt: null },
          { firstChunkStartedAt: { isSet: false } },
        ],
      },
      data: { firstChunkStartedAt: new Date() },
    }),
  ]);

  const eventId =
    communication.templateV2.scopeId ?? chunk.dispatch.eventId ?? null;
  const config = communication.templateV2.config as {
    notification?: { title?: string; body?: string };
  } | null;
  const templateTitle = config?.notification?.title ?? "";
  const templateBody = config?.notification?.body ?? "";
  const profiles = await prisma.profile.findMany({
    where: { id: { in: chunk.recipientProfileIds } },
    select: {
      id: true,
      email: true,
      name: true,
      phoneNumber: true,
      userId: true,
    },
  });
  const profileById = new Map(profiles.map((profile) => [profile.id, profile]));
  const event = eventId
    ? await prisma.event.findUnique({
        where: { id: eventId },
        select: {
          whiteLabelId: true,
          translations: { select: { name: true }, take: 1 },
        },
      })
    : null;
  const emails = [
    ...new Set(
      profiles.flatMap((profile) => {
        const email = profile.email?.trim().toLowerCase();
        return email ? [email] : [];
      })
    ),
  ];
  const profileUserIds = profiles.flatMap((profile) =>
    profile.userId ? [profile.userId] : []
  );
  const users = await prisma.user.findMany({
    where: {
      OR: [
        ...(profileUserIds.length ? [{ id: { in: profileUserIds } }] : []),
        ...(event?.whiteLabelId && emails.length
          ? [
              {
                accessibleWhiteLabelId: event.whiteLabelId,
                email: { in: emails },
              },
            ]
          : []),
      ],
    },
    select: { id: true, email: true },
  });
  const userById = new Map(users.map((user) => [user.id, user]));
  const userByEmail = new Map(
    users.map((user) => [user.email.trim().toLowerCase(), user])
  );
  const tokens = eventId
    ? await prisma.deviceToken.findMany({
        where: { eventId, userId: { in: users.map((user) => user.id) } },
        select: { userId: true, token: true },
      })
    : [];
  const tokensByUserId = new Map<string, string[]>();
  for (const token of tokens) {
    const existing = tokensByUserId.get(token.userId) ?? [];
    existing.push(token.token);
    tokensByUserId.set(token.userId, existing);
  }
  const preflight = classifyNotificationPreflight({
    eventId,
    title: templateTitle,
    body: templateBody,
  });

  await runBounded(
    chunk.recipientProfileIds,
    recipientConcurrency(),
    async (recipientProfileId) => {
      const profile = profileById.get(recipientProfileId);
      const loginEmail = profile?.email?.trim().toLowerCase();
      const user =
        (loginEmail ? userByEmail.get(loginEmail) : undefined) ??
        (profile?.userId ? userById.get(profile.userId) : undefined);
      const base = {
        dispatchId: input.dispatchId,
        chunkId: chunk.id,
        recipientProfileId,
        communicationSettingsId: communication.id,
        templateV2Id: communication.templateV2Id!,
        occurrenceKey: chunk.dispatch.occurrenceKey,
        email: profile?.email,
        phoneNumber: profile?.phoneNumber,
      };
      const failBoth = async (reason: string) => {
        await failDelivery({ ...base, provider: Provider.IN_APP, reason });
        await failDelivery({ ...base, provider: Provider.EXPO, reason });
      };
      try {
        if (!profile) {
          await failBoth("Recipient profile not found");
          return;
        }
        if (preflight.action === "fail-campaign" || !eventId) {
          await failBoth("Missing notification content or eventId");
          return;
        }
        if (!user) {
          await failBoth("Profile is not linked to a user");
          return;
        }
        const { title, body } = resolvePushNotificationMergeTags(
          { title: templateTitle, body: templateBody },
          {
            recipientName: profile.name,
            sessionName: communication.triggerSession?.translations[0]?.name,
            sessionLocation:
              communication.triggerSession?.translations[0]?.location,
            eventName: event?.translations[0]?.name,
          }
        );
        await sendInApp({
          ...base,
          receiverUserId: user.id,
          eventId,
          title,
          body,
        });
        await sendExpo({
          ...base,
          receiverUserId: user.id,
          tokens: tokensByUserId.get(user.id) ?? [],
          title,
          body,
        });
      } catch (error) {
        const reason = error instanceof Error ? error.message : String(error);
        await failBoth(reason);
      }
    }
  );
  await finishCommunicationBroadcastChunk(
    input.dispatchId,
    chunk.id,
    chunk.recipientProfileIds
  );
}

export async function failCommunicationBroadcastChunk(
  input: { dispatchId: string; chunkId: string },
  reason: string
) {
  const chunk = await prisma.communicationBroadcastChunk.findUnique({
    where: { id: input.chunkId },
    select: {
      recipientProfileIds: true,
      dispatch: {
        select: {
          occurrenceKey: true,
          communicationSettings: {
            select: { id: true, templateV2Id: true },
          },
        },
      },
    },
  });
  if (!chunk?.dispatch.communicationSettings.templateV2Id) return;
  for (const recipientProfileId of chunk.recipientProfileIds) {
    const base = {
      dispatchId: input.dispatchId,
      chunkId: input.chunkId,
      recipientProfileId,
      communicationSettingsId: chunk.dispatch.communicationSettings.id,
      templateV2Id: chunk.dispatch.communicationSettings.templateV2Id,
      occurrenceKey: chunk.dispatch.occurrenceKey,
      reason,
    };
    await failDelivery({ ...base, provider: Provider.IN_APP });
    await failDelivery({ ...base, provider: Provider.EXPO });
  }
  await finishCommunicationBroadcastChunk(
    input.dispatchId,
    input.chunkId,
    chunk.recipientProfileIds
  );
}

export async function getCommunicationBroadcastProgress(
  communicationSettingsId: string
) {
  const dispatch = await prisma.communicationBroadcastDispatch.findFirst({
    where: { communicationSettingsId },
    orderBy: { createdAt: "desc" },
    select: {
      id: true,
      occurrenceKey: true,
      status: true,
      expectedCount: true,
      resolvedCount: true,
      duplicatesRemoved: true,
      queuedCount: true,
      terminalCount: true,
      inAppSucceededCount: true,
      inAppFailedCount: true,
      expoAcceptedCount: true,
      expoFailedCount: true,
      expoNoTokenCount: true,
      expoTicketsAccepted: true,
      expoTicketsRejected: true,
      retryCount: true,
      failedReason: true,
      startedAt: true,
      firstChunkStartedAt: true,
      completedAt: true,
      createdAt: true,
      updatedAt: true,
      chunks: {
        select: {
          status: true,
          expectedCount: true,
          terminalCount: true,
        },
      },
    },
  });
  if (!dispatch) return null;
  const processing = dispatch.chunks
    .filter(
      (chunk) => chunk.status === CommunicationBroadcastChunkStatus.PROCESSING
    )
    .reduce((sum, chunk) => sum + chunk.expectedCount - chunk.terminalCount, 0);
  return {
    ...dispatch,
    processing,
    unaccounted: Math.max(0, dispatch.expectedCount - dispatch.terminalCount),
    chunkCount: dispatch.chunks.length,
    chunks: undefined,
  };
}
